import { useMemo, useState } from 'react';

import type { ChatCardItem, ExtractedFact } from '@karehero/models';

import type { NeedItem } from 'config/onboardingV4/needs';
import { needItems } from 'utils/onboardingV4Messages';
import type { Person } from 'utils/person';
import { useFactPreview } from './useFactPreview';

/** What a draft ticked as it was sent, held until the messages it became are read. */
interface HeldDraft {
  messageIDs: string[];
  facts: readonly ExtractedFact[];
}

export interface UseNeedChecklistOptions {
  careCircleId?: string;
  /** What is needed, each ticked by its fact. Keep stable: a new list checks what is said again. */
  items: readonly NeedItem[];
  /** The facts read so far. */
  facts: readonly ExtractedFact[];
  person: Person;
  /** Whether they are sending what we need, so the first left is marked next. */
  isGathering: boolean;
  /** Whether what we need has been asked about, so what they say may answer it. */
  isOpen: boolean;
  /** What is typed or recorded but not yet sent. */
  liveText: string;
  /** What they are answering, as it was asked. */
  context: string;
  /** Where what they say is from, as it is checked. */
  source: string;
  isEnabled: boolean;
  isRead: (messageID: string) => boolean;
}

/**
 * useNeedChecklist ticks off what we would need as it is read: from the facts,
 * from what they are still saying, and from what they sent until it is read.
 */
export const useNeedChecklist = ({
  careCircleId,
  items: needed,
  facts,
  person,
  isGathering,
  isOpen,
  liveText,
  context,
  source,
  isEnabled,
  isRead,
}: UseNeedChecklistOptions) => {
  const lookedFor = useMemo(() => needed.map((item) => item.fact), [needed]);
  // What they are still saying ticks too, and unticks if they change or
  // cancel it; once sent, its ticks are held until what was sent is read.
  const draftFacts = useFactPreview({
    careCircleId,
    facts: lookedFor,
    said: isOpen ? liveText : '',
    context,
    source,
    isEnabled: isEnabled && isOpen,
  });
  const [heldDrafts, setHeldDrafts] = useState<HeldDraft[]>([]);

  /** Keeps a sent draft's ticks until the messages it became are read. */
  const holdDraft = (messageIDs: string[]) => {
    if (!isOpen || !draftFacts.length) return;
    setHeldDrafts((held) => [...held, { messageIDs, facts: draftFacts }]);
  };

  const heldFacts = useMemo(
    () =>
      heldDrafts
        .filter((held) => !held.messageIDs.every(isRead))
        .flatMap((held) => held.facts),
    [heldDrafts, isRead],
  );
  const items = useMemo<ChatCardItem[]>(
    () =>
      needItems({
        items: needed,
        facts: [...facts, ...heldFacts, ...draftFacts],
        person,
        isGathering,
      }),
    [draftFacts, facts, heldFacts, isGathering, needed, person],
  );

  return { items, holdDraft };
};
